import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const semNcm = (p) => !p.ncm || /^0+$/.test(String(p.ncm).replace(/\D/g, '')) || String(p.ncm).replace(/\D/g, '').length !== 8;
const CHUNK = 10;

export const NcmBulkAI = ({ open, onClose, products, toast, onApplied }) => {
  const alvo = useMemo(() => products.filter(semNcm), [products]);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) { setRows([]); setProgress(0); setRunning(false); }
  }, [open]);

  const processar = async () => {
    setRunning(true);
    setRows([]);
    setProgress(0);
    const acc = [];
    for (let i = 0; i < alvo.length; i += CHUNK) {
      const lote = alvo.slice(i, i + CHUNK);
      try {
        const r = await api.post('/fiscal/ncm/sugerir-lote', {
          produtos: lote.map((p) => ({ id: p.id, nome: p.name, descricao: p.description || undefined, categoria: p.category || undefined })),
        });
        for (const it of r.data.itens) acc.push({ ...it, selected: !!it.ncm });
      } catch (e) {
        for (const p of lote) acc.push({ id: p.id, nome: p.name, ncm: '', descricao: 'Falha na IA — preencha manualmente', confianca: 0, selected: false });
      }
      setRows([...acc]);
      setProgress(Math.min(alvo.length, i + CHUNK));
    }
    setRunning(false);
  };

  const update = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const selecionados = rows.filter((r) => r.selected && r.ncm.replace(/\D/g, '').length === 8);

  const aplicar = async () => {
    if (!selecionados.length) { toast.error('Nenhum produto selecionado com NCM válido'); return; }
    setApplying(true);
    try {
      const r = await api.post('/products/ncm-lote', { items: selecionados.map((s) => ({ id: s.id, ncm: s.ncm.replace(/\D/g, '') })) });
      toast.success(`NCM aplicado em ${r.data.aplicados} produto(s)`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar NCM');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !running && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="ncm-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> NCM em lote com IA</DialogTitle>
          <DialogDescription>
            {alvo.length} produto(s) sem NCM válido. A IA sugere o código de cada um; revise, edite e escolha quais aplicar.
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 && !running && (
          <div className="py-10 text-center space-y-4">
            {alvo.length === 0 ? (
              <p className="text-slate-600 flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Todos os produtos já têm NCM.</p>
            ) : (
              <Button onClick={processar} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white" data-testid="ncm-bulk-start">
                <Sparkles className="w-4 h-4 mr-2" /> Sugerir NCM para {alvo.length} produto(s)
              </Button>
            )}
          </div>
        )}

        {(running || rows.length > 0) && (
          <div className="space-y-3 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center gap-3 text-sm text-slate-600">
              {running ? <Loader2 className="w-4 h-4 animate-spin text-violet-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              <span data-testid="ncm-bulk-progress">{running ? `Processando ${progress}/${alvo.length}...` : `${rows.length} sugestões prontas`}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-violet-600 transition-all" style={{ width: `${alvo.length ? (progress / alvo.length) * 100 : 0}%` }} /></div>
            </div>
            <div className="flex-1 overflow-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"><input type="checkbox" checked={rows.length > 0 && rows.every((r) => r.selected)} onChange={(e) => setRows(rows.map((r) => ({ ...r, selected: e.target.checked && !!r.ncm })))} data-testid="ncm-bulk-select-all" /></TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead className="w-36">NCM sugerido</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="w-24 text-right">Confiança</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} data-testid={`ncm-bulk-row-${r.id}`} className={r.selected ? '' : 'opacity-60'}>
                      <TableCell><input type="checkbox" checked={r.selected} onChange={(e) => update(r.id, { selected: e.target.checked })} data-testid={`ncm-bulk-check-${r.id}`} /></TableCell>
                      <TableCell className="font-medium text-sm">{r.nome}</TableCell>
                      <TableCell><Input value={r.ncm} maxLength={8} onChange={(e) => update(r.id, { ncm: e.target.value.replace(/\D/g, '') })} className="font-mono h-8" data-testid={`ncm-bulk-input-${r.id}`} /></TableCell>
                      <TableCell className="text-xs text-slate-600">{r.descricao}{r.catalogado === false && r.ncm && <span className="ml-1 text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />não catalogado</span>}</TableCell>
                      <TableCell className="text-right text-sm">{r.confianca ? `${Math.round(r.confianca * 100)}%` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm text-slate-500">{selecionados.length} de {rows.length} selecionado(s)</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={running || applying}>Cancelar</Button>
                <Button onClick={aplicar} disabled={running || applying || !selecionados.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ncm-bulk-apply">
                  {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Aplicar {selecionados.length} NCM(s)
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
